import { createFeatureSelector, createSelector } from "@ngrx/store";
import { OrganizationsState } from "./organizations.state";
import { adapter } from "./organizations.reducers";

export const selectOrganizationState = createFeatureSelector<OrganizationsState>('organizations');

export const {
  selectEntities: selectOrganizationEntities,
  selectAll: selectAllOrganizations,
  selectTotal: selectAllOrganizationsCount,
} = adapter.getSelectors(selectOrganizationState);

export const selectMemberTenantIds = createSelector(selectOrganizationState, s => s?.memberTenantIds || []);

// Primary selector: returns only organizations the user is a member of
// This preserves the original invariant that selectOrganizations returns accessible orgs
export const selectOrganizations = createSelector(
  selectAllOrganizations,
  selectMemberTenantIds,
  (orgs, memberTenantIds) => {
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    return orgs.filter(o => memberTenantIds.includes(o.tenantId)).sort(sortByName);
  }
);

export const selectOrganizationsCount = createSelector(
  selectOrganizations,
  orgs => orgs?.length || 0);

// Returns all organizations sorted: member orgs first (by name), then non-member orgs (by name)
export const selectAllOrganizationsSorted = createSelector(
  selectAllOrganizations,
  selectMemberTenantIds,
  (orgs, memberTenantIds) => {
    const sortByName = (a, b) => (a.name || '').localeCompare(b.name || '');
    const memberOrgs = orgs.filter(o => memberTenantIds.includes(o.tenantId)).sort(sortByName);
    const nonMemberOrgs = orgs.filter(o => !memberTenantIds.includes(o.tenantId)).sort(sortByName);
    return [...memberOrgs, ...nonMemberOrgs];
  }
);

export const selectSelectedOrganization = createSelector(selectOrganizationState, s => s?.selectedOrganization);
export const selectSelectedOrganizationName = createSelector(selectSelectedOrganization, org => org?.name || 'current organization');
export const selectSelectedOrganizationTenantId = createSelector(selectSelectedOrganization, org => org.tenantId);

export const selectMyOrganizationLoaded = createSelector(selectOrganizationState, s => s?.myOrganizationLoaded);

export const selectSelectedOrganizationIsExternal = createSelector(selectSelectedOrganization, org => !!org?.externalEntraIdDomainName?.trim());

export const selectOrganizationsLoading = createSelector(selectOrganizationState, s => s?.loadingOrganizations);
export const selectOrganizationsLoaded = createSelector(selectOrganizationState, s => s?.organizationsLoaded);

export const selectOrganizationById = (id: number) =>
  createSelector(
    selectOrganizationEntities,
    entities => entities[id]
  );